import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Autocomplete from 'react-google-autocomplete';

interface GooglePlacesInputProps {
    value?: string;
    onChange?: (value: string, place?: google.maps.places.PlaceResult) => void;
    placeholder?: string;
    size?: 'small' | 'middle' | 'large';
    className?: string;
    disabled?: boolean;
}

type SizeType = 'small' | 'middle' | 'large';

const StyledAutocomplete = styled(Autocomplete) <{ $size?: SizeType }>`
  &.ant-input {
    border-radius: ${({ theme }) => theme.radius};
    border: 1px solid #aebeca;
    padding: ${({ $size }) => {
        switch ($size) {
            case 'large':
                return '11px 12px';
            case 'small':
                return '4px 8px';
            default:
                return '7px 12px';
        }
    }};
    font-size: ${({ $size }) => {
        switch ($size) {
            case 'large':
                return '16px';
            case 'small':
                return '12px';
            default:
                return '14px';
        }
    }};
    line-height: 1.5;
    width: 100%;
    box-sizing: border-box;
    transition: all 0.3s;

    &:disabled {
      color: #627d95;
      border: solid 1px #aebeca;
      background-color: ${({ theme }) => theme.lozengeColor};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      border: solid 1px ${({ theme }) => theme.hoverColor};
    }

    &:focus {
      box-shadow: 0 0 8px 0 ${({ theme }) => theme.primaryColor};
      border-color: transparent;
      outline: none;
    }

    &::placeholder {
      color: #aebeca;
    }
  }
`;

const StyledInput = styled.input<{ $size?: SizeType }>`
  border-radius: ${({ theme }) => theme.radius};
  border: 1px solid #aebeca;
  padding: ${({ $size }) => {
        switch ($size) {
            case 'large':
                return '11px 12px';
            case 'small':
                return '4px 8px';
            default:
                return '7px 12px';
        }
    }};
  font-size: ${({ $size }) => {
        switch ($size) {
            case 'large':
                return '16px';
            case 'small':
                return '12px';
            default:
                return '14px';
        }
    }};
  line-height: 1.5;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.3s;

  &:disabled {
    color: #627d95;
    border: solid 1px #aebeca;
    background-color: ${({ theme }) => theme.lozengeColor};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border: solid 1px ${({ theme }) => theme.hoverColor};
  }

  &:focus {
    box-shadow: 0 0 8px 0 ${({ theme }) => theme.primaryColor};
    border-color: transparent;
    outline: none;
  }

  &::placeholder {
    color: #aebeca;
  }
`;

// Simple debounce function
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

const GooglePlacesInput: React.FC<GooglePlacesInputProps> = ({
    value,
    onChange,
    placeholder,
    size = 'middle',
    className,
    disabled = false,
    ...rest
}) => {
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Check if Google Maps API is loaded
        const checkGoogleMaps = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                setIsGoogleLoaded(true);
            } else {
                // Retry after a short delay
                setTimeout(checkGoogleMaps, 100);
            }
        };

        checkGoogleMaps();
    }, []);

    // Update input value when prop changes
    useEffect(() => {
        if (inputRef.current && inputRef.current.value !== (value || '')) {
            inputRef.current.value = value || '';
        }
    }, [value]);

    // Debounced onChange for manual typing
    const debouncedOnChange = useMemo(
        () => debounce((newValue: string) => {
            if (onChange && newValue) {
                onChange(newValue, {
                    name: newValue,
                    formatted_address: newValue,
                    place_id: '',
                    geometry: undefined
                } as google.maps.places.PlaceResult);
            }
        }, 300),
        [onChange]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Use debounced onChange for manual typing
        debouncedOnChange(newValue);
    };

    const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
        if (place && (place.name || place.formatted_address)) {
            const cityName = place.name || place.formatted_address || '';
            if (inputRef.current) {
                inputRef.current.value = cityName;
            }
            if (onChange) {
                onChange(cityName, place);
            }
        }
    };

    // Fallback to regular input if Google Maps isn't loaded or if disabled
    if (!isGoogleLoaded || disabled) {
        return (
            <StyledInput
                ref={inputRef}
                className={`ant-input ${className || ''}`}
                $size={size}
                placeholder={placeholder}
                defaultValue={value}
                onChange={handleInputChange}
                disabled={disabled}
                {...rest}
            />
        );
    }

    return (
        <StyledAutocomplete
            ref={inputRef}
            className={`ant-input ${className || ''}`}
            $size={size}
            placeholder={placeholder}
            defaultValue={value}
            onPlaceSelected={handlePlaceSelected}
            onChange={handleInputChange}
            options={{
                types: ['(cities)'],
                fields: ['name', 'formatted_address', 'place_id', 'geometry']
            }}
            {...rest}
        />
    );
};

export default GooglePlacesInput; 